<template>
  <div class="verification-container">
    <div class="page-header">
      <h2>消费验证管理</h2>
      <p class="subtitle">审核用户消费记录，验证位置和金额</p>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon pending"><el-icon><Clock /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.pending }}</div>
            <div class="stat-label">待审核</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon verified"><el-icon><CircleCheck /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.verified }}</div>
            <div class="stat-label">已通过</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon rejected"><el-icon><CircleClose /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.rejected }}</div>
            <div class="stat-label">已拒绝</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon amount"><el-icon><Money /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">¥{{ stats.todayAmount || 0 }}</div>
            <div class="stat-label">今日金额</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 搜索筛选 -->
    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" :model="queryParams">
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="待审核" value="pending" />
            <el-option label="已通过" value="verified" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker v-model="queryParams.dateRange" type="daterange" start-placeholder="开始日期" end-placeholder="结束日期" style="width: 240px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadList">搜索</el-button>
          <el-button @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never" class="table-card">
      <el-table :data="tableData" v-loading="loading" stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="用户" width="120">
          <template #default="{ row }">{{ row.user?.realName || row.user?.mobile || '-' }}</template>
        </el-table-column>
        <el-table-column label="商户" width="150">
          <template #default="{ row }">{{ row.merchant?.name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="amount" label="消费金额" width="120">
          <template #default="{ row }">¥{{ row.amount }}</template>
        </el-table-column>
        <el-table-column prop="verificationStatus" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="statusMap[row.verificationStatus]?.type">{{ statusMap[row.verificationStatus]?.label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="locationInfo" label="位置信息" min-width="200" show-overflow-tooltip />
        <el-table-column prop="createdAt" label="创建时间" width="170">
          <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button v-if="row.verificationStatus === 'pending'" type="success" size="small" @click="handleVerify(row)">通过</el-button>
            <el-button v-if="row.verificationStatus === 'pending'" type="danger" size="small" @click="handleReject(row)">拒绝</el-button>
            <el-button type="primary" size="small" link @click="showDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrapper">
        <el-pagination v-model:current-page="queryParams.page" v-model:page-size="queryParams.pageSize" :total="total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next" @change="loadList" />
      </div>
    </el-card>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailVisible" title="消费验证详情" width="600px">
      <el-descriptions :column="2" border v-if="currentItem">
        <el-descriptions-item label="ID">{{ currentItem.id }}</el-descriptions-item>
        <el-descriptions-item label="状态"><el-tag :type="statusMap[currentItem.verificationStatus]?.type">{{ statusMap[currentItem.verificationStatus]?.label }}</el-tag></el-descriptions-item>
        <el-descriptions-item label="用户">{{ currentItem.user?.realName || currentItem.user?.mobile }}</el-descriptions-item>
        <el-descriptions-item label="商户">{{ currentItem.merchant?.name }}</el-descriptions-item>
        <el-descriptions-item label="消费金额">¥{{ currentItem.amount }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(currentItem.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="位置信息" :span="2">{{ currentItem.locationInfo }}</el-descriptions-item>
        <el-descriptions-item label="拒绝原因" :span="2" v-if="currentItem.rejectionReason">{{ currentItem.rejectionReason }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <!-- 拒绝原因弹窗 -->
    <el-dialog v-model="rejectVisible" title="拒绝原因" width="400px">
      <el-input v-model="rejectReason" type="textarea" :rows="3" placeholder="请输入拒绝原因" />
      <template #footer>
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmReject">确认拒绝</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Clock, CircleCheck, CircleClose, Money } from '@element-plus/icons-vue'
import { consumptionVerificationApi } from '@/api/modules/consumption-verification'
import { useStore } from 'vuex'

const store = useStore()
const userStore = computed(() => store.getters['auth/adminInfo'])
const loading = ref(false)
const tableData = ref([])
const total = ref(0)
const stats = ref({ total: 0, pending: 0, verified: 0, rejected: 0, todayAmount: 0 })
const queryParams = ref({ page: 1, pageSize: 10, status: '', dateRange: null })
const detailVisible = ref(false)
const currentItem = ref(null)
const rejectVisible = ref(false)
const rejectReason = ref('')
const rejectItem = ref(null)

const statusMap = {
  pending: { label: '待审核', type: 'warning' },
  verified: { label: '已通过', type: 'success' },
  rejected: { label: '已拒绝', type: 'danger' }
}

const formatDate = (date) => date ? new Date(date).toLocaleString('zh-CN') : '-'

const loadList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams.value }
    if (params.dateRange && params.dateRange.length === 2) {
      params.startDate = params.dateRange[0]
      params.endDate = params.dateRange[1]
      delete params.dateRange
    }
    const res = await consumptionVerificationApi.getList(params)
    if (res.data?.code === 200) {
      tableData.value = res.data.data.items || []
      total.value = res.data.data.total || 0
    }
  } finally {
    loading.value = false
  }
}

const loadStats = async () => {
  const res = await consumptionVerificationApi.getStatistics()
  if (res.data?.code === 200) {
    stats.value = res.data.data
  }
}

const resetQuery = () => {
  queryParams.value = { page: 1, pageSize: 10, status: '', dateRange: null }
  loadList()
}

const handleVerify = async (row) => {
  const res = await consumptionVerificationApi.verify(row.id, userStore.value?.id)
  if (res.data?.code === 200) {
    ElMessage.success('审核通过')
    loadList()
    loadStats()
  }
}

const handleReject = (row) => {
  rejectItem.value = row
  rejectReason.value = ''
  rejectVisible.value = true
}

const confirmReject = async () => {
  if (!rejectReason.value) {
    ElMessage.warning('请输入拒绝原因')
    return
  }
  const res = await consumptionVerificationApi.reject(rejectItem.value.id, userStore.value?.id, rejectReason.value)
  if (res.data?.code === 200) {
    ElMessage.success('已拒绝')
    rejectVisible.value = false
    loadList()
    loadStats()
  }
}

const showDetail = async (row) => {
  const res = await consumptionVerificationApi.getDetail(row.id)
  if (res.data?.code === 200) {
    currentItem.value = res.data.data
    detailVisible.value = true
  }
}

onMounted(() => {
  loadList()
  loadStats()
})
</script>

<style scoped>
.verification-container { padding: 20px; }
.page-header { margin-bottom: 20px; }
.page-header h2 { margin: 0; font-size: 24px; font-weight: 600; color: #1f2937; }
.subtitle { margin: 8px 0 0; color: #6b7280; font-size: 14px; }
.stat-cards { margin-bottom: 20px; }
.stat-card { display: flex; align-items: center; padding: 20px; border-radius: 12px; }
.stat-card :deep(.el-card__body) { display: flex; align-items: center; width: 100%; padding: 0; }
.stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 24px; margin-right: 16px; }
.stat-icon.pending { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #d97706; }
.stat-icon.verified { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #059669; }
.stat-icon.rejected { background: linear-gradient(135deg, #fee2e2, #fecaca); color: #dc2626; }
.stat-icon.amount { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #2563eb; }
.stat-content { flex: 1; }
.stat-value { font-size: 24px; font-weight: 700; color: #1f2937; }
.stat-label { font-size: 14px; color: #6b7280; margin-top: 4px; }
.filter-card { margin-bottom: 20px; border-radius: 12px; }
.table-card { border-radius: 12px; }
.pagination-wrapper { display: flex; justify-content: flex-end; margin-top: 20px; }
</style>
